import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Button, Card, Spinner } from 'react-bootstrap';
import { Upload, Download, Trash2, File, Pencil } from 'lucide-react';
import { ProjectModel } from '../../../types/projects';
import { Attachment } from '../../../types/attachments';
import { getProjectAttachmentDownloadUrl } from '../../../Helpers/ProjectAttachmentUtils';
import { AttachmentNameModal } from '../modals/AttachmentNameModal';
import { ConfirmDeleteModal } from '../../../Components/ConfirmDeleteModal';
import { RenameAttachmentModal } from '../../../Components/RenameAttachmentModal';
import { UploadPickerModal } from '../../../Components/UploadPickerModal';
import { MetadataInfoButton } from '../../../Components/MetadataInfoButton';
import { useUserNames } from '../../../Helpers/useUserNames';

interface ProjectAttachmentsSectionProps {
    project: ProjectModel;
    onUploadAttachment: (file: File, displayName?: string) => Promise<boolean>;
    /** Called once when the naming queue finishes and at least one upload succeeded. */
    onUploadsComplete?: () => void | Promise<void>;
    onDeleteAttachment: (attachmentKey: string) => Promise<void>;
    /** Throws on failure; the rename modal shows the error. */
    onRenameAttachment: (attachmentKey: string, displayName: string | undefined) => Promise<void>;
    submitting?: boolean;
}

export const ProjectAttachmentsSection: React.FC<ProjectAttachmentsSectionProps> = ({
    project,
    onUploadAttachment,
    onUploadsComplete,
    onDeleteAttachment,
    onRenameAttachment,
    submitting = false,
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [renaming, setRenaming] = useState<{ key: string; attachment: Attachment } | null>(null);
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showNameModal, setShowNameModal] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteAttachmentKey, setPendingDeleteAttachmentKey] = useState<string | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    const { userNames, loadUserNames } = useUserNames();

    const attachments = useMemo(
        () => project.attachments ? Object.entries(project.attachments) : [],
        [project.attachments]
    );

    // Extract UIDs for user name loading
    const attachmentUids = useMemo(
        () => attachments.map(([, attachment]) => {
            if (attachment.attachmentType === 'file') {
                return attachment.uploadedBy;
            } else {
                return attachment.author;
            }
        }).filter(Boolean) as string[],
        [attachments]
    );

    // Load user names for all attachments
    useEffect(() => {
        if (attachmentUids.length > 0) {
            loadUserNames(attachmentUids);
        }
    }, [attachmentUids, loadUserNames]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOver(false);
        }
    }, []);

    const uploadedCountRef = useRef(0);

    const handleFiles = useCallback((files: File[]) => {
        if (files.length > 0) {
            uploadedCountRef.current = 0;
            setPendingFiles(files);
            setCurrentFileIndex(0);
            setShowNameModal(true);
        }
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        handleFiles(Array.from(e.dataTransfer.files));
    }, [handleFiles]);

    // Close the naming queue and refresh once if anything was uploaded.
    // A per-file refresh can unmount this section mid-queue, wiping the
    // pending files — so the refresh must wait until the queue is done.
    const finishQueue = useCallback(async () => {
        setShowNameModal(false);
        setPendingFiles([]);
        setCurrentFileIndex(0);
        if (uploadedCountRef.current > 0) {
            uploadedCountRef.current = 0;
            await onUploadsComplete?.();
        }
    }, [onUploadsComplete]);

    const handleNameConfirm = useCallback(async (displayName: string | undefined) => {
        if (pendingFiles.length === 0) return;

        setUploading(true);

        try {
            const success = await onUploadAttachment(pendingFiles[currentFileIndex], displayName);

            if (success) {
                uploadedCountRef.current += 1;
                if (currentFileIndex < pendingFiles.length - 1) {
                    setCurrentFileIndex(currentFileIndex + 1);
                } else {
                    await finishQueue();
                }
            } else {
                await finishQueue();
            }
        } catch (err) {
            console.error('Error uploading attachment:', err);
            await finishQueue();
        } finally {
            setUploading(false);
        }
    }, [pendingFiles, currentFileIndex, onUploadAttachment, finishQueue]);

    const handleCloseModal = () => {
        void finishQueue();
    };

    const handleDownload = async (storagePath: string) => {
        try {
            const url = await getProjectAttachmentDownloadUrl(storagePath);
            window.open(url, '_blank');
        } catch (err) {
            console.error('Error downloading attachment:', err);
        }
    };

    const handleDeleteAttachmentClick = (attachmentKey: string) => {
        setPendingDeleteAttachmentKey(attachmentKey);
        setShowDeleteModal(true);
    };

    const handleConfirmDeleteAttachment = async () => {
        if (pendingDeleteAttachmentKey) {
            await onDeleteAttachment(pendingDeleteAttachmentKey);
            setPendingDeleteAttachmentKey(null);
        }
    };

    const handleRenameConfirm = async (displayName: string | undefined) => {
        if (!renaming) return;
        setRenameSubmitting(true);
        try {
            await onRenameAttachment(renaming.key, displayName);
            setRenaming(null);
        } finally {
            setRenameSubmitting(false);
        }
    };

    const truncateFileName = (name: string, maxLength: number = 30): string => {
        if (name.length <= maxLength) return name;
        const lastDot = name.lastIndexOf('.');
        if (lastDot === -1) {
            return name.substring(0, maxLength) + '...';
        }
        const extension = name.substring(lastDot);
        const nameWithoutExt = name.substring(0, lastDot);
        if (nameWithoutExt.length <= maxLength) return name;
        return nameWithoutExt.substring(0, maxLength) + '...' + extension;
    };

    return (
        <div style={{
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #dee2e6'
        }}>
            <div style={{ border: "1px solid #dee2e6", borderRadius: "10px", backgroundColor: "#fff" }}>
                <div style={{
                    backgroundColor: '#f8f9fa',
                    padding: '12px 16px',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h5 style={{ margin: 0 }}>Bilagor</h5>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowUploadModal(true)}
                        >
                            <Upload size={14} className="me-1" />
                            Ladda upp fil
                        </Button>
                    </div>
                </div>
                <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        border: dragOver ? '2px dashed #007bff' : '2px dashed transparent',
                        borderRadius: '8px',
                        backgroundColor: dragOver ? '#f0f8ff' : 'transparent',
                        transition: 'all 0.3s ease',
                        padding: dragOver ? '22px' : '24px',
                        minHeight: '100px',
                        position: 'relative'
                    }}
                >
                    {uploading && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            padding: '20px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Spinner animation="border" size="sm" />
                            <span>Laddar upp bilagor...</span>
                        </div>
                    )}
                    {attachments.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {attachments.map(([attachmentKey, attachment]) => {
                                const fileName = attachment.fileName || attachment.key;
                                const displayName = attachment.displayName || fileName;
                                const fileType = attachment.attachmentType === 'file'
                                    ? attachment.fileType?.toUpperCase() || 'FIL'
                                    : attachment.mimeType?.split('/')[1]?.toUpperCase() || 'BILD';

                                const truncatedFileName = truncateFileName(fileName);
                                const showTruncatedFileName = attachment.displayName && displayName !== fileName;

                                return (
                                    <Card
                                        key={attachmentKey}
                                        style={{
                                            border: '1px solid #dee2e6',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            e.currentTarget.style.borderColor = '#0d6efd';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#fff';
                                            e.currentTarget.style.borderColor = '#dee2e6';
                                        }}
                                        onClick={() => handleDownload(attachment.storagePath)}
                                    >
                                        <Card.Body style={{ padding: '12px 16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <File size={20} style={{ color: '#666', flexShrink: 0 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{
                                                        fontWeight: '500',
                                                        fontSize: '14px',
                                                        marginBottom: '4px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {displayName}
                                                    </div>
                                                    <small style={{ color: '#666', fontSize: '12px' }}>
                                                        {fileType}
                                                        {showTruncatedFileName && ` • ${truncatedFileName}`}
                                                        {attachment.attachmentType === 'file' && attachment.uploadedAt && ` • Uppladdad: ${attachment.uploadedAt}`}
                                                        {attachment.attachmentType === 'image' && attachment.date && ` • Datum: ${attachment.date}`}
                                                    </small>
                                                </div>
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <MetadataInfoButton
                                                        entries={[
                                                            {
                                                                label: attachment.attachmentType === 'file' ? 'Uppladdad' : 'Tillagd',
                                                                date: attachment.attachmentType === 'file' ? attachment.uploadedAt : attachment.date,
                                                                userName: userNames[attachment.attachmentType === 'file' ? attachment.uploadedBy : attachment.author] || 'Okänd användare',
                                                            },
                                                        ]}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    title="Byt namn"
                                                    aria-label={`Byt namn på ${displayName}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setRenaming({ key: attachmentKey, attachment });
                                                    }}
                                                    disabled={submitting}
                                                >
                                                    <Pencil size={14} />
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    title="Radera"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteAttachmentClick(attachmentKey);
                                                    }}
                                                    disabled={submitting}
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                                <Download size={18} style={{ color: '#666', flexShrink: 0 }} />
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{
                            textAlign: 'center',
                            padding: dragOver ? '20px' : '40px',
                            color: dragOver ? '#007bff' : '#666',
                            fontStyle: 'italic',
                            transition: 'all 0.3s ease'
                        }}>
                            {dragOver ? (
                                <div>
                                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>📁</div>
                                    <div>Släpp filer här</div>
                                </div>
                            ) : (
                                <div>
                                    <img
                                        src="/Images/UI/file.png"
                                        alt="File"
                                        style={{
                                            width: '35px',
                                            height: 'auto',
                                            display: 'block',
                                            margin: '0 auto 8px auto'
                                        }}
                                    />
                                    <div>Inga bilagor ännu. Dra och släpp filer här eller klicka på "Ladda upp fil".</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                title="Ladda upp fil"
                multiple
            />

            <AttachmentNameModal
                show={showNameModal}
                onHide={handleCloseModal}
                onConfirm={handleNameConfirm}
                files={pendingFiles}
                currentIndex={currentFileIndex}
                submitting={uploading}
            />

            <RenameAttachmentModal
                show={renaming !== null}
                fileName={renaming?.attachment.fileName || renaming?.key || ''}
                displayName={renaming?.attachment.displayName}
                onHide={() => setRenaming(null)}
                onConfirm={handleRenameConfirm}
                submitting={renameSubmitting}
            />

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => {
                    setShowDeleteModal(false);
                    setPendingDeleteAttachmentKey(null);
                }}
                onConfirm={handleConfirmDeleteAttachment}
                title="Radera bilaga"
                message="Är du säker på att du vill radera denna bilaga? Detta kan inte ångras."
            />
        </div>
    );
};
